import type { AxiosRequestConfig } from 'axios';

import { isExpectedPayrollError, payrollErrorCode } from './payrollError';
import { PAYROLL_COMMAND_REFUSED_ACTION, payrollCommandRefusalOf } from './payrollCommandRefusal';

import { collectiveApiMutator, type ErrorType as SharedErrorType } from 'services/api/collectiveApiMutator';
import type { DomainErrorHandler } from 'services/api/domainErrorHandler';
import { requestOwnerConfirmation } from 'services/payroll/ownerConfirmation';
import { sendDatadogRumAction } from 'utils/analytics/datadogRum';

// A refusal the UI explains goes to its own RUM action rather than a RUM error. A write refused
// because no employee is known to be the member asks them which one is, wherever they are.
const payrollErrorHandler: DomainErrorHandler = {
    isExpected: (error) => isExpectedPayrollError(error) || payrollCommandRefusalOf(error) !== null,
    onError: (error) => {
        const refusal = payrollCommandRefusalOf(error);
        if (refusal) {
            sendDatadogRumAction(PAYROLL_COMMAND_REFUSED_ACTION, refusal);
        }
        if (payrollErrorCode(error) === 'OWNER_NOT_FOUND') {
            requestOwnerConfirmation();
        }
    },
};

// A function, not a factory's return value: orval reads this signature to give each generated
// function its `options` argument, which is how payroll writes send `Idempotency-Key`.
export const payrollApiMutator = <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> =>
    collectiveApiMutator<T>({ ...config, domainErrorHandler: payrollErrorHandler }, options);

// Declared rather than re-exported: orval only finds an `ErrorType` declared in the mutator file.
export type ErrorType<Error> = SharedErrorType<Error>;
